import * as H from "history";

import type { IApiError } from "src/library/utils/errors/apiError";
import * as AT from "src/core/shared/store/global/global.constants";
import type { IRole } from "src/core/shared/api/roles.api";
import type { IUser } from "src/core/users/api/users.api";
import { ContextType } from "src/core/shared/hooks/menu/useMenu";
import type {
  IGlobalContextModal,
  IAppSettings,
  IGlobalStore,
  MasqueradeMode,
  LatestPackSize
} from "src/core/shared/store/global/global.reducer";
import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";
import { cache } from "src/core/shared/hooks/useCache";

export type GlobalActions =
  | ISetGlobalUser
  | ISetGlobalRole
  | ISetGlobalUserStatus
  | ISetGlobalError
  | ISetGlobalModal
  | ISetAppSettings
  | IPurgeMenu
  | IAddLocation
  | ISetGlobalUserName
  | IPurgeUserRole
  | ISetSurveyTracking
  | IToggleJobsQueue
  | ISetMasqueradeMode
  | IAddLastestPackSize;

interface ISetGlobalUser {
  type: AT.SET_GLOBAL_USER;
  user: IUser | null;
  role?: IRole | null;
}

export const setGlobalUser = (user: (IUser & { role?: IRole }) | null, skipRole = false): ISetGlobalUser => {
  // If skipRole has been requested, only update the user object
  if (skipRole) {
    return {
      type: AT.SET_GLOBAL_USER,
      user
    };
  }
  let role;

  if (user?.role) {
    role = user?.role;
    delete user.role;
  }

  return {
    type: AT.SET_GLOBAL_USER,
    user,
    role
  };
};

interface ISetGlobalRole {
  type: AT.SET_GLOBAL_ROLE;
  role: IRole | null;
}

export const setGlobalRole = (role: IRole | null): ISetGlobalRole => {
  return {
    type: AT.SET_GLOBAL_ROLE,
    role
  };
};

interface ISetGlobalUserStatus {
  type: AT.SET_GLOBAL_USER_STATUS;
  userSuspended: boolean;
}

export const setGlobalUserStatus = (suspended: boolean): ISetGlobalUserStatus => {
  return {
    type: AT.SET_GLOBAL_USER_STATUS,
    userSuspended: suspended
  };
};

interface ISetGlobalUserName {
  type: AT.SET_GLOBAL_USER_NAME;
  user: IUser;
}

export const setGlobalUserName = (user: IUser): ISetGlobalUserName => {
  return {
    type: AT.SET_GLOBAL_USER_NAME,
    user
  };
};

interface IPurgeMenu {
  type: AT.PURGE_MENU;
}

export const purgeMenu = (): IPurgeMenu => {
  return { type: AT.PURGE_MENU };
};

interface IPurgeUserRole {
  type: AT.PURGE_USER_ROLE;
}

export const purgeUserRole = (): IPurgeUserRole => {
  return { type: AT.PURGE_USER_ROLE };
};

interface ISetGlobalError {
  type: AT.SET_GLOBAL_ERROR;
  error: IApiError | null;
}

export const setGlobalError = (error: IApiError | null): ISetGlobalError => {
  return {
    type: AT.SET_GLOBAL_ERROR,
    error
  };
};

interface ISetGlobalModal {
  type: AT.SET_GLOBAL_MODAL;
  modalData: IGlobalContextModal | null;
}

export const setGlobalModal = (modalData: IGlobalContextModal | null): ISetGlobalModal => {
  return {
    type: AT.SET_GLOBAL_MODAL,
    modalData
  };
};

interface ISetAppSettings {
  type: AT.SET_APP_SETTINGS;
  settings: IAppSettings;
}

export const setAppSettings = (settings: IAppSettings, isRequest = false): ISetAppSettings => {
  // if its not the main GET REQUEST
  if (!isRequest) {
    cache().then(cache => {
      cache.removeItem(GLOBAL_SETTINGS_CACHE_KEY);
    });
  }

  return {
    type: AT.SET_APP_SETTINGS,
    settings
  };
};

interface IAddLocation {
  type: AT.ADD_LOCATION;
  location: H.Location<{ context?: ContextType }>
}

export const addLocation = (location: H.Location<{ context?: ContextType }>): IAddLocation => {
  return {
    type: AT.ADD_LOCATION,
    location
  };
};

interface ISetSurveyTracking {
  type: AT.SET_SURVEY_TRACKING;
  surveyTracking: IGlobalStore["surveyTracking"];
}

export const setSurveyTracking = (surveyTracking: IGlobalStore["surveyTracking"]): ISetSurveyTracking => {
  return {
    type: AT.SET_SURVEY_TRACKING,
    surveyTracking
  };
};

interface IToggleJobsQueue {
  type: AT.SHOW_JOB_QUEUE;
  show: boolean;
}

export const toggleJobsQueue = (show: boolean): IToggleJobsQueue => {
  return {
    type: AT.SHOW_JOB_QUEUE,
    show
  };
};

interface ISetMasqueradeMode {
  type: AT.MASQUERADE_MODE;
  masqueradeMode: MasqueradeMode;
}

export const setMasqueradeMode = (masqueradeMode: MasqueradeMode): ISetMasqueradeMode => {
  return {
    type: AT.MASQUERADE_MODE,
    masqueradeMode
  };
};

interface IAddLastestPackSize {
  type: AT.ADD_LATEST_PACK_SIZE;
  latestPackSize: LatestPackSize;
}

export const addLatestPackSize = (latestPackSize: LatestPackSize): IAddLastestPackSize => {
  return {
    type: AT.ADD_LATEST_PACK_SIZE,
    latestPackSize
  };
};
